/**
 * Click funnel metrics — aggregate GA4 event counts + optional Firestore fallback.
 */

import {
  CLICK_FUNNEL_STEPS,
  normalizeClickEventName,
} from "./clickAnalyticsEvents";
import { normalizeProductEventName } from "./productAnalyticsEvents";

function eventTimestampMs(ev) {
  const raw = ev?.created_at ?? ev?.createdAt ?? ev?.timestamp;
  if (raw == null) return 0;
  if (typeof raw.toMillis === "function") return raw.toMillis();
  if (typeof raw === "object" && typeof raw.seconds === "number") return raw.seconds * 1000;
  if (typeof raw === "number" && raw > 1e11) return raw;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Sum counts per normalized event name from GA4 API rows. */
export function indexGa4EventCounts(ga4Rows = []) {
  const map = new Map();
  for (const row of ga4Rows) {
    const name = normalizeClickEventName(row?.eventName ?? row?.dimensionValues?.[0]?.value);
    const count = Math.max(0, Number(row?.count ?? row?.metricValues?.[0]?.value ?? 0));
    if (!name || !Number.isFinite(count)) continue;
    map.set(name, (map.get(name) || 0) + count);
  }
  return map;
}

/** Count events from `product_analytics_events` (backfill / client writes). */
export function countEventsFromFirestore(productAnalyticsEvents = [], { startMs = 0, endMs = Infinity } = {}) {
  const map = new Map();
  for (const ev of productAnalyticsEvents) {
    const name = normalizeClickEventName(ev?.event_name ?? ev?.eventName);
    if (!name) continue;
    const ms = eventTimestampMs(ev);
    if (ms > 0 && (ms < startMs || ms > endMs)) continue;
    map.set(name, (map.get(name) || 0) + 1);
  }
  return map;
}

/** Per-step total = sum of all alias event counts. */
export function stepClickCount(step, countsByEvent = new Map()) {
  let total = 0;
  for (const ev of step.events) {
    const n = normalizeClickEventName(ev);
    total += countsByEvent.get(n) || 0;
  }
  return total;
}

/**
 * Build funnel stages with drop-off from aggregate click counts.
 * @param {Map<string, number>} countsByEvent
 */
export function computeClickFunnel(countsByEvent = new Map()) {
  const steps = CLICK_FUNNEL_STEPS;
  const stages = steps.map((step, index) => {
    const value = stepClickCount(step, countsByEvent);
    const prevValue = index > 0 ? stepClickCount(steps[index - 1], countsByEvent) : null;
    const dropOffPct =
      prevValue != null && prevValue > 0 && value <= prevValue
        ? ((prevValue - value) / prevValue) * 100
        : null;
    const stepConvPct = prevValue != null && prevValue > 0 ? (value / prevValue) * 100 : null;
    const eventBreakdown = step.events
      .map((ev) => ({
        event: ev,
        count: countsByEvent.get(normalizeClickEventName(ev)) || 0,
      }))
      .filter((r) => r.count > 0 || step.instrumented);
    return {
      key: step.key,
      label: step.label,
      description: step.description,
      instrumented: step.instrumented,
      value,
      dropOffPct,
      stepConvPct,
      eventBreakdown,
      events: step.events,
    };
  });

  const first = stages[0]?.value ?? 0;
  const last = stages[stages.length - 1]?.value ?? 0;
  const overallConvPct = first > 0 ? (last / first) * 100 : null;

  const missingInstrumentation = CLICK_FUNNEL_STEPS.filter((s) => !s.instrumented).map((s) => s.label);

  return {
    stages,
    mainPathStages: stages,
    overallConvPct,
    totalClicks: [...countsByEvent.values()].reduce((a, b) => a + b, 0),
    missingInstrumentation,
    computedAt: Date.now(),
  };
}

/** Merge GA4 counts with Firestore counts (GA4 wins when both present). */
export function mergeClickCountMaps(ga4Map = new Map(), firestoreMap = new Map()) {
  const merged = new Map(firestoreMap);
  for (const [k, v] of ga4Map) {
    if (v > 0) merged.set(k, v);
  }
  return merged;
}

/** Daily series from GA4 rows with date dimension. */
export function buildDailyClickSeries(ga4DailyRows = []) {
  const byDate = new Map();
  for (const row of ga4DailyRows) {
    const date =
      row?.date ??
      row?.dimensionValues?.[1]?.value ??
      row?.dimensionValues?.[0]?.value;
    const name = normalizeClickEventName(
      row?.eventName ?? row?.dimensionValues?.[0]?.value ?? row?.dimensionValues?.[1]?.value
    );
    const count = Math.max(0, Number(row?.count ?? row?.metricValues?.[0]?.value ?? 0));
    if (!date || String(date).length < 8) continue;
    const key = String(date);
    if (!byDate.has(key)) byDate.set(key, { date: key, total: 0, byEvent: {} });
    const bucket = byDate.get(key);
    bucket.total += count;
    bucket.byEvent[name] = (bucket.byEvent[name] || 0) + count;
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/** Firestore product events that match click funnel names. */
export function isClickAnalyticsFirestoreEvent(doc) {
  const n = normalizeClickEventName(doc?.event_name ?? doc?.eventName);
  if (!n) return false;
  return CLICK_FUNNEL_STEPS.some((step) =>
    step.events.some((e) => normalizeClickEventName(e) === n || normalizeProductEventName(e) === n)
  );
}
